// Переказ між власними картками: «Купити долари / євро» і «На мою …» з
// «Переказ на картку». Однакова валюта — 1:1, різна — обмін за курсом monobank
// (кеш currency_rates_cache, кладе App.jsx): списання з картки-джерела,
// зарахування на валютну картку і по транзакції на кожній. Транзакції —
// категорія ADD («Поповнення картки») з operationAmount / operationCurrency
// / rate, тож деталі показують «У валюті 1.00 ₴, курс 45.0410».
import { ref, push, remove } from 'firebase/database';
import { database } from './firebaseConfig';
import { MAIN_CARD_ID } from './cardsStore';
import { adjustBalance, txPath } from './transferStore';

// Ліміт НБУ на купівлю валюти за гривні — на календарний місяць
export const FX_MONTH_LIMIT = 200000;
const FX_IMG = '/transfer/fx-card.png';

// «З Чорної картки», «На доларову картку»
const GEN = {
  black: 'Чорної картки', white: 'Білої картки', usd: 'доларової картки', eur: 'єврової картки',
  kids: 'Дитячої картки', diia: 'Дія.Картки', cashback: 'картки Національний кешбек',
  evidnovlennia: 'картки єВідновлення',
};
const ACC = {
  black: 'Чорну картку', white: 'Білу картку', kids: 'Дитячу картку', diia: 'Дія.Картку',
  cashback: 'картку Національний кешбек', evidnovlennia: 'картку єВідновлення',
  usd: 'доларову картку', eur: 'єврову картку',
};
export const fromLabel = (type) => `З ${GEN[type] || 'картки'}`;
export const toLabel = (type) => ACC[type] || 'картку';

export const cardCurrency = (card) => (card.id === MAIN_CARD_ID ? 'UAH' : card.currency || 'UAH');

export function readRates() {
  try {
    const r = JSON.parse(localStorage.getItem('currency_rates_cache')) || {};
    return {
      USD: { buy: Number(r.usdBuy) || 0, sell: Number(r.usdSell) || 0 },
      EUR: { buy: Number(r.eurBuy) || 0, sell: Number(r.eurSell) || 0 },
    };
  } catch (e) {
    return { USD: { buy: 0, sell: 0 }, EUR: { buy: 0, sell: 0 } };
  }
}

// Скільки одиниць валюти-джерела коштує одиниця цільової: банк продає
// цільову за sell, купує валюту-джерело за buy (гривня — 1). 0 — курсу нема.
export function fxRate(src, dst, rates) {
  if (src === dst) return 1;
  const sell = dst === 'UAH' ? 1 : rates[dst]?.sell;
  const buy = src === 'UAH' ? 1 : rates[src]?.buy;
  return sell && buy ? sell / buy : 0;
}

// Курс для людей — завжди «гривень за валюту» (продаж долара за гривні:
// rate < 1 → показуємо 1/rate), як «курс 44.7000» у monobank
export const displayRate = (rate) => (rate && rate < 1 ? 1 / rate : rate);

// Гривні, витрачені на валюту цього місяця (поле fxBuyUah вихідних транзакцій)
export function fxSpentThisMonth(txLists) {
  const now = new Date();
  let sum = 0;
  txLists.forEach((list) => (list || []).forEach((tx) => {
    if (!tx || !tx.fxBuyUah) return;
    const d = new Date(tx.date);
    if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) sum += Number(tx.fxBuyUah) || 0;
  }));
  return sum;
}

// from/to — картки ({ id, type, currency }), src/dst — суми у їхніх валютах.
// Кидає Error('insufficient_funds'). Повертає дані для cancelFx.
export async function buyCurrency({ chatId, from, to, src, dst, rate }) {
  const fromCur = cardCurrency(from);
  const toCur = cardCurrency(to);
  const exchange = fromCur !== toCur;
  if (!(await adjustBalance(chatId, from.id, -src))) throw new Error('insufficient_funds');

  const now = new Date().toISOString();
  const rateStr = displayRate(rate).toFixed(4);
  const outgoing = {
    id: Date.now(),
    name: `На ${toLabel(to.type)}`,
    img: FX_IMG,
    amount: `-${src.toFixed(2)}`,
    date: now,
    type: 'expense',
    category: 'ADD',
  };
  // Обмін — з курсом і сумою в іншій валюті (деталі: «У валюті …, курс …»)
  if (exchange) Object.assign(outgoing, { fx: true, operationAmount: dst.toFixed(2), operationCurrency: toCur, rate: rateStr });
  // Купівля валюти за гривні — рахується в місячний ліміт
  if (fromCur === 'UAH' && toCur !== 'UAH') outgoing.fxBuyUah = src.toFixed(2);
  const outRef = await push(ref(database, txPath(chatId, from.id)), outgoing);

  await adjustBalance(chatId, to.id, dst);
  const incoming = {
    id: Date.now() + 1,
    name: fromLabel(from.type),
    img: FX_IMG,
    amount: dst.toFixed(2),
    date: now,
    type: 'income',
    category: 'ADD',
  };
  if (exchange) Object.assign(incoming, { fx: true, operationAmount: src.toFixed(2), operationCurrency: fromCur, rate: rateStr });
  const inRef = await push(ref(database, txPath(chatId, to.id)), incoming);

  return {
    chatId, from, to, src, dst, tx: outgoing,
    outPath: `${txPath(chatId, from.id)}/${outRef.key}`,
    inPath: `${txPath(chatId, to.id)}/${inRef.key}`,
  };
}

// «Скасувати платіж» — повертаємо обидві картки як були
export async function cancelFx(t) {
  await adjustBalance(t.chatId, t.from.id, t.src, true);
  await remove(ref(database, t.outPath));
  await adjustBalance(t.chatId, t.to.id, -t.dst, true);
  await remove(ref(database, t.inPath));
}

// Транзакція купівлі валюти (нові — з fx: true, старіші — за іконкою)
export const isFxTx = (tx) => Boolean(tx?.fx) || /\/transfer\/fx-/.test(String(tx?.img || ''));

// Як monobank показує списання при купівлі валюти у виписці: «Переказ на
// картку» з кружком у кольорі валютної картки. Решта — як є.
const FX_LIST_IMG = { USD: '/transfer/fx-list-usd.png', EUR: '/transfer/fx-list-eur.png' };
export function listView(tx) {
  if (isFxTx(tx) && Number(tx.amount) < 0 && FX_LIST_IMG[tx.operationCurrency]) {
    return { name: 'Переказ на картку', img: FX_LIST_IMG[tx.operationCurrency] };
  }
  return { name: tx.name, img: tx.img };
}
